import type { ActionProperties, ActionTemplateParameterResource, EnvironmentV2Resource, GitDependencyReference, PackageReference, StepPackageInputs } from "@octopusdeploy/octopus-server-client";
import type * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useAccountsFromContext, useRefreshAccountsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessAccountsContextProvider";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { useGitCredentialsFromContext, useRefreshGitCredentialsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { useGitHubConnectionsFromContext, useRefreshGitHubConnectionsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitHubConnectionsContextProvider";
import type { ActionTemplateActionInputsDependencies } from "~/components/ActionInputs/ActionTemplateActionInputs";
import { ActionPropertiesEditor } from "~/components/ActionPropertiesEditor/ActionPropertiesEditor";
import { enforceNewActionFeatures } from "~/components/ActionPropertiesEditor/enforceNewActionFeatures";
import type { ActionEditProps, ActionPlugin } from "~/components/Actions/pluginRegistry";
import type { FieldErrors } from "~/components/DataBaseComponent/Errors";
import FeatureEditor from "~/components/FeatureEditor/FeatureEditor";
import type { ActionTemplateStepInputDependencies } from "~/components/StepPackageEditor/StepInputDependencies";
interface ActionTemplateEditorAndFeaturesProps {
    localNames: string[];
    plugin: ActionPlugin;
    inputs?: StepPackageInputs;
    properties: ActionProperties;
    packages: Array<PackageReference>;
    gitDependencies?: GitDependencyReference[];
    environments: EnvironmentV2Resource[];
    errors: FieldErrors | undefined; // only used for shouldComponentUpdate
    busy: Promise<unknown> | boolean | undefined;
    parameters: ActionTemplateParameterResource[];
    getFieldError(field: string): string;
    setInputs(inputs: StepPackageInputs, callback?: () => void): void;
    setProperties(properties: Partial<ActionProperties>, initialise?: boolean, callback?: () => void): void;
    setPackages(packages: Array<PackageReference>, initialise?: boolean): void;
    setGitDependencies?(gitDependencies: Array<GitDependencyReference>): void;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
    isNew: boolean;
    hasFeatures: boolean;
    openFeaturesElement?: JSX.Element;
    enabledFeatures: string;
}
const MergedSectionsPluginEditor: React.FC<ActionEditProps> = (actionEditProps: ActionEditProps) => {
    const { top: TopSection, default: DefaultSection } = actionEditProps.plugin.editSections;
    return (<>
            {TopSection && <TopSection {...actionEditProps}/>}
            {DefaultSection && <DefaultSection {...actionEditProps}/>}
        </>);
};
MergedSectionsPluginEditor.displayName = "MergedSectionsPluginEditor"
export function ActionTemplatePropertiesEditorAndFeatures(props: ActionTemplateEditorAndFeaturesProps) {
    const { packages, setPackages, parameters, localNames, gitDependencies, setGitDependencies } = props;
    useEffect(() => {
        const properties = { ...props.properties };
        const enabledFeatures = enforceNewActionFeatures(props.plugin, props.properties, props.isNew);
        if (!!enabledFeatures) {
            // Server will strip empty properties, so only set this if we need to, otherwise this triggers our save-changes dialog unnecessarily.
            properties["Octopus.Action.EnabledFeatures"] = enforceNewActionFeatures(props.plugin, props.properties, props.isNew);
        }
        props.setProperties(properties, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const accounts = useAccountsFromContext();
    const refreshAccounts = useRefreshAccountsFromContext();
    const gitCredentials = useGitCredentialsFromContext();
    const refreshGitCredentials = useRefreshGitCredentialsFromContext();
    const gitHubConnections = useGitHubConnectionsFromContext();
    const refreshGitHubConnections = useRefreshGitHubConnectionsFromContext();
    const stableSetPackages = useCallback((getUpdatedPackages: (prev: Array<PackageReference>) => Array<PackageReference>) => setPackages(getUpdatedPackages(packages)), [packages, setPackages]);
    const stableSetGitDependencies = useCallback((getUpdatedGitDependencies: (prev: GitDependencyReference[]) => GitDependencyReference[]) => setGitDependencies?.(getUpdatedGitDependencies(gitDependencies ?? [])), [gitDependencies, setGitDependencies]);
    const stableRefreshFeeds = useCallback(async () => {
        await refreshFeeds();
    }, [refreshFeeds]);
    const stableRefreshAccounts = useCallback(async () => {
        await refreshAccounts();
    }, [refreshAccounts]);
    const stableRefreshGitCredentials = useCallback(async () => {
        await refreshGitCredentials();
    }, [refreshGitCredentials]);
    const stableRefreshGitHubConnections = useCallback(async () => {
        await refreshGitHubConnections();
    }, [refreshGitHubConnections]);
    const dependencies = useMemo<ActionTemplateStepInputDependencies>(() => ({
        feeds: feeds,
        refreshFeeds: stableRefreshFeeds,
        parameters: parameters,
        setPackages: stableSetPackages,
        setGitDependencies: stableSetGitDependencies,
        localNames: localNames,
        accounts,
        refreshAccounts: stableRefreshAccounts,
        gitCredentials: gitCredentials,
        refreshGitCredentials: stableRefreshGitCredentials,
        gitHubConnections: gitHubConnections,
        refreshGitHubConnections: stableRefreshGitHubConnections,
    }), [feeds, stableRefreshFeeds, parameters, stableSetPackages, stableSetGitDependencies, localNames, accounts, stableRefreshAccounts, gitCredentials, stableRefreshGitCredentials, gitHubConnections, stableRefreshGitHubConnections]);
    if (!props.plugin.editSections) {
        throw new Error(`Plugin ${props.plugin.actionType} is missing 'editSections' property`);
    }
    const actionInputsDependencies: ActionTemplateActionInputsDependencies = {
        type: "action-template",
        setPackages: props.setPackages,
        expandedByDefault: props.isNew,
        parameters: dependencies.parameters,
        packages: props.packages,
        feeds: dependencies.feeds,
        environments: props.environments,
        refreshFeeds: dependencies.refreshFeeds,
        getFieldError: props.getFieldError,
        localNames: dependencies.localNames,
        accounts: dependencies.accounts,
        refreshAccounts: dependencies.refreshAccounts,
        properties: props.properties,
        setProperties: props.setProperties,
    };
    return (<>
            <ActionPropertiesEditor actionInputsDependencies={actionInputsDependencies} inputDependencies={dependencies} plugin={props.plugin} editor={MergedSectionsPluginEditor} properties={props.properties} packages={packages} gitDependencies={gitDependencies} doBusyTask={props.doBusyTask} busy={props.busy} inputs={props.inputs} setInputs={props.setInputs} setProperties={props.setProperties} setGitDependencies={props.setGitDependencies} setPackages={setPackages} getFieldError={props.getFieldError} errors={props.errors} expandedByDefault={props.isNew}/>

            {props.hasFeatures && (<FeatureEditor actionInputsDependencies={actionInputsDependencies} plugin={props.plugin} isNew={props.isNew} doBusyTask={props.doBusyTask} busy={props.busy} properties={props.properties} packages={props.packages} setProperties={props.setProperties} setPackages={props.setPackages} setGitDependencies={props.setGitDependencies} enabledFeatures={props.enabledFeatures} getFieldError={props.getFieldError} localNames={props.localNames} errors={props.errors} expandedByDefault={props.isNew} openFeaturesElement={props.openFeaturesElement}/>)}
        </>);
}
